import React, { useState } from 'react';
import { signInWithEmailAndPassword, signInWithCustomToken } from 'firebase/auth';
import { collection, query, where, getDocs, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../App';
import { appId } from '../config/firebaseConfig';
import { hashPassword } from '../utils/cryptoUtils';
import TechBackground from '../components/TechBackground';
import PasswordInput from '../components/common/PasswordInput';

export default function LoginPage({ onPortalLoginSuccess }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestData, setRequestData] = useState({ name: '', email: '', company: '' });
    const [requestStatus, setRequestStatus] = useState({ type: '', msg: '' });
    const [requestLoading, setRequestLoading] = useState(false);

    const handleRequestAccess = async (e) => {
        e.preventDefault();
        setRequestStatus({ type: '', msg: '' });
        setRequestLoading(true);

        try {
            const paths = {
                tickets: `/artifacts/${appId}/public/data/tickets`,
                counter: `/artifacts/${appId}/public/data/counters/ticket_counter`
            };

            await runTransaction(db, async (transaction) => {
                const counterDoc = await transaction.get(doc(db, paths.counter));
                const newTicketNumber = (counterDoc.data()?.currentId || 0) + 1;

                transaction.set(doc(collection(db, paths.tickets)), {
                    title: 'Solicitação de Acesso - Novo Usuário',
                    description: `Solicitação de acesso\nNome: ${requestData.name}\nE-mail: ${requestData.email}\nEmpresa: ${requestData.company}`,
                    clientName: requestData.name,
                    clientEmail: requestData.email,
                    clientCompany: requestData.company,
                    numeroSequencial: newTicketNumber,
                    status: 'Cadastro Novo Usuário',
                    priority: 'medium',
                    createdAt: serverTimestamp(),
                    createdByEmail: requestData.email,
                    isPublic: true
                });

                transaction.update(doc(db, paths.counter), { currentId: newTicketNumber });
            });

            setRequestStatus({ type: 'success', msg: 'Solicitação de acesso enviada, os dados de acesso será enviado por e-mail' });
            setRequestData({ name: '', email: '', company: '' });
            setTimeout(() => {
                setShowRequestModal(false);
                setRequestStatus({ type: '', msg: '' });
            }, 5000);
        } catch (error) {
            console.error("Erro ao solicitar acesso:", error);
            setRequestStatus({ type: 'error', msg: 'Erro ao enviar solicitação. Tente novamente mais tarde.' });
        } finally {
            setRequestLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const cleanEmail = email.trim().toLowerCase();

        // 1. Tenta autenticar primeiro no Firebase Auth (Equipe Arcus)
        try {
            await signInWithEmailAndPassword(auth, cleanEmail, password);
            return;
        } catch (authErr) {
            // 2. Se não autenticar no Firebase Auth, verifica se é Usuário do Portal no Firestore
            try {
                const inputHash = await hashPassword(password);

                // Tentativa A: via Cloud Function dedicada (autenticação segura server-side + Custom Token)
                try {
                    const functions = getFunctions();
                    const autenticarPortalFn = httpsCallable(functions, 'autenticarUsuarioPortal');
                    const fnRes = await autenticarPortalFn({ email: cleanEmail, passwordHash: inputHash });

                    if (fnRes?.data?.success && fnRes?.data?.user) {
                        const sessionData = fnRes.data.user;
                        localStorage.setItem('arcus_portal_user', JSON.stringify(sessionData));

                        if (fnRes.data.customToken) {
                            try {
                                await signInWithCustomToken(auth, fnRes.data.customToken);
                            } catch (tokErr) {
                                console.warn("Aviso ao autenticar token customizado no Firebase Auth:", tokErr);
                            }
                        }

                        if (onPortalLoginSuccess) {
                            onPortalLoginSuccess(sessionData);
                        } else {
                            window.location.reload();
                        }
                        return;
                    }
                } catch (fnErr) {
                    if (fnErr?.code === 'functions/permission-denied') {
                        setError('Seu acesso ao portal está desativado. Entre em contato com a equipe Arcus.');
                        return;
                    }
                    if (fnErr?.code === 'functions/unauthenticated' || fnErr?.code === 'functions/not-found') {
                        if (fnErr?.message?.includes('Credenciais inválidas')) {
                            setError('E-mail ou senha inválidos. Por favor, tente novamente.');
                            return;
                        }
                    }
                    // Se a Cloud Function não estiver disponível/implantada, tenta consulta direta no Firestore
                    console.log("Cloud Function não disponível ou pendente de deploy, verificando Firestore direto:", fnErr?.message);
                }

                // Tentativa B: Fallback direto no Firestore
                const portalUsersPath = `/artifacts/${appId}/public/data/portal_users`;
                const q = query(collection(db, portalUsersPath), where('email', '==', cleanEmail));
                const snap = await getDocs(q);

                if (!snap.empty) {
                    const userDoc = snap.docs[0];
                    const userData = { id: userDoc.id, ...userDoc.data() };

                    if (userData.active === false) {
                        setError('Seu acesso ao portal está desativado. Entre em contato com a equipe Arcus.');
                        return;
                    }

                    if (userData.passwordHash && userData.passwordHash === inputHash) {
                        // Sucesso no login do Portal!
                        const sessionData = {
                            id: userData.id,
                            name: userData.name,
                            email: userData.email,
                            role: userData.role || 'User',
                            companyId: userData.companyId,
                            companyName: userData.companyName,
                            companyType: userData.companyType,
                            phone: userData.phone || '',
                            isPortalUser: true
                        };
                        localStorage.setItem('arcus_portal_user', JSON.stringify(sessionData));
                        if (onPortalLoginSuccess) {
                            onPortalLoginSuccess(sessionData);
                        } else {
                            window.location.reload();
                        }
                        return;
                    }
                }
            } catch (firestoreErr) {
                console.error("Erro ao verificar usuário do portal:", firestoreErr);
                if (firestoreErr?.code === 'permission-denied' || firestoreErr?.message?.includes('Missing or insufficient permissions')) {
                    setError('Acesso bloqueado por permissão do Firebase. Adicione a regra para portal_users no Firebase Console.');
                    return;
                }
            }

            setError('E-mail ou senha inválidos. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen flex text-white overflow-hidden font-sans">
            <TechBackground />
            
            {/* Decoration Section (Left Side) - Hidden on mobile, visible on LG+ */}
            <div className="hidden lg:flex flex-1 items-center justify-center p-20 relative pointer-events-none">
                <div className="absolute top-20 left-20 text-left">
                    <div className="text-[120px] font-black text-white/5 leading-none">ARCUS</div>
                </div>
            </div>

            {/* Login Section (Right Side) */}
            <div className="relative z-10 w-full max-w-xl flex flex-col justify-center p-8 sm:p-12 md:p-20 bg-slate-900/40 backdrop-blur-2xl border-l border-white/5 shadow-2xl animate-in slide-in-from-right-20 duration-1000 ml-auto">
                <div className="mb-12">
                    <div className="flex items-center gap-4 mb-8">
                        <img src="/logo.png" alt="Arcus Logo" className="w-16 h-16 object-contain" />
                        <div>
                            <h1 className="text-4xl font-black tracking-tighter text-white">
                                AR<span className="text-cyan-400">CUS</span>
                            </h1>
                            <p className="text-[10px] font-bold text-cyan-500/60 uppercase tracking-[0.3em]">Gestão de Infraestrutura</p>
                        </div>
                    </div>
                    
                    <h2 className="text-3xl font-bold mb-2">Bem-vindo de volta</h2>
                    <p className="text-slate-400">Introduza as suas credenciais para aceder ao sistema central.</p>
                </div>

                <form className="space-y-8" onSubmit={handleLogin}>
                    <div className="space-y-6">
                        <div className="group relative">
                            <label htmlFor="email" className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2 transition-colors group-focus-within:text-cyan-400">E-mail Corporativo</label>
                            <input 
                                id="email" 
                                type="email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                                required 
                                placeholder="exemplo@arcus.pt"
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-4 px-5 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all focus:bg-slate-800" 
                            />
                        </div>
                        
                        <div className="group relative">
                            <label htmlFor="password" className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2 transition-colors group-focus-within:text-cyan-400">Senha de Acesso</label>
                            <PasswordInput 
                                id="password" 
                                name="password" 
                                value={password} 
                                onChange={(e) => setPassword(e.target.value)} 
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl text-sm flex items-center gap-3 animate-in fade-in duration-300">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <div className="pt-4 flex flex-col gap-3">
                        <button 
                            type="submit" 
                            disabled={loading} 
                            className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-black py-4 px-6 rounded-xl shadow-xl shadow-cyan-900/20 transition-all active:scale-[0.98] disabled:bg-slate-700 disabled:text-slate-500 flex items-center justify-center gap-2 group"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    Autenticar no Sistema
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </>
                            )}
                        </button>

                        <button 
                            type="button"
                            onClick={() => setShowRequestModal(true)}
                            className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 px-6 rounded-xl transition-all border border-slate-700 hover:border-slate-600 text-sm"
                        >
                            Solicitar criação de acesso
                        </button>
                    </div>
                </form>

                <div className="mt-auto pt-20">
                    <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest text-center sm:text-right underline decoration-cyan-500/30">
                        &copy; {new Date().getFullYear()} Arcus Informática • Sistema de Informação Restrito
                    </p>
                </div>
            </div>

            {showRequestModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl relative">
                        <button 
                            onClick={() => setShowRequestModal(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-white"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        
                        <h3 className="text-xl font-bold text-white mb-6">Solicitar Acesso</h3>
                        
                        <form onSubmit={handleRequestAccess} className="space-y-4">
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Nome</label>
                                <input 
                                    required 
                                    type="text"
                                    value={requestData.name}
                                    onChange={(e) => setRequestData({...requestData, name: e.target.value})}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">E-mail</label>
                                <input 
                                    required 
                                    type="email"
                                    value={requestData.email}
                                    onChange={(e) => setRequestData({...requestData, email: e.target.value})}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Empresa</label>
                                <input 
                                    required 
                                    type="text"
                                    value={requestData.company}
                                    onChange={(e) => setRequestData({...requestData, company: e.target.value})}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                                />
                            </div>

                            {requestStatus.msg && (
                                <div className={`p-3 rounded-lg text-xs font-bold ${requestStatus.type === 'success' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                    {requestStatus.msg}
                                </div>
                            )}

                            <button 
                                type="submit" 
                                disabled={requestLoading}
                                className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 text-white font-bold py-3 px-6 rounded-xl transition-all mt-4"
                            >
                                {requestLoading ? 'Enviando...' : 'Solicitação'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}